import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import {
  BarChart3,
  Calendar,
  ClipboardList,
  Dumbbell,
  Plus,
  RotateCcw,
  Scale,
  Settings,
  TableOfContents,
  User,
  Zap
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { DISCORD_INVITE_URL } from '../lib/constants'
import { DashboardSection, SidebarItem } from '../types'
import { DraggableSidebarItem } from './DraggableSidebarItem'
import Discord from './icons/Discord'
import { SidebarSectionsSkeleton } from './ui/loading'
import { Tooltip } from './ui/tooltip'
import { UnifiedButton } from './ui/unified-button'

interface SidebarProps {
  collapsedSections: Record<string, boolean>
  onToggleSection: (section: DashboardSection) => void
  onScrollToSection: (section: string) => void
  orderedSections: SidebarItem[]
  isDragging: boolean
  onDragStart: () => void
  onDragEnd: (activeId: string, overId: string) => void
  onResetOrder: () => void
  onRemoveSection?: (sectionId: DashboardSection) => void
  showDashboardSections?: boolean
  isMobileOpen?: boolean
  onMobileClose?: () => void
  sectionsLoading?: boolean
  bottomSlot?: ReactNode
  hideDiscord?: boolean
}

const _DASHBOARD_SECTIONS: SidebarItem[] = [
  {
    id: 'calendar',
    label: 'Calendar',
    icon: Calendar,
    description: 'Workout calendar and schedule'
  },
  {
    id: 'weight',
    label: 'Weight',
    icon: Scale,
    description: 'Weight tracking and trends'
  },
  {
    id: 'routine',
    label: 'Training Split',
    icon: ClipboardList,
    description: 'Workout routine configuration'
  },
  {
    id: 'weeklyStats',
    label: 'Weekly Sets',
    icon: BarChart3,
    description: 'Weekly muscle group statistics'
  },
  {
    id: 'muscleGroups',
    label: 'Muscle Groups',
    icon: Dumbbell,
    description: 'Charts and progressive overload index'
  }
]

export const Sidebar = ({
  collapsedSections,
  onToggleSection,
  onScrollToSection,
  orderedSections,
  isDragging: _isDragging,
  onDragStart,
  onDragEnd,
  onResetOrder,
  onRemoveSection,
  showDashboardSections = true,
  isMobileOpen = false,
  onMobileClose,
  sectionsLoading = false,
  bottomSlot,
  hideDiscord = false
}: SidebarProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const handleSectionClick = (section: SidebarItem) => {
    // Close mobile sidebar when section is clicked
    if (onMobileClose) {
      onMobileClose()
    }

    // Check if section is visible on screen
    const _refs = {
      calendar: 'calendar',
      weight: 'weight',
      routine: 'routine',
      weeklyStats: 'weeklyStats',
      muscleGroups: 'muscleGroups'
    }

    // Find the section element by looking for elements with ref attributes or data attributes
    const sectionElement = document.querySelector(`[data-section="${section.id}"]`)

    if (sectionElement) {
      const rect = sectionElement.getBoundingClientRect()
      const viewportHeight = window.innerHeight

      // Consider visible if any part of the section is in the viewport
      const isVisible = rect.bottom > 0 && rect.top < viewportHeight

      if (isVisible) {
        // Section is visible, toggle collapse state
        onToggleSection(section.id)
      } else {
        // Section is not visible, scroll to it
        onScrollToSection(section.id)
      }
    } else {
      // Fallback: just scroll to section
      onScrollToSection(section.id)
    }
  }

  const handleNavigate = (path: string) => {
    navigate(path)
    // Close mobile sidebar when navigating
    if (onMobileClose) {
      onMobileClose()
    }
  }

  const handleDragStartEvent = (_event: DragStartEvent) => {
    onDragStart()
  }

  const handleDragEndEvent = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      onDragEnd(active.id.toString(), over.id.toString())
    } else {
      onDragEnd(active.id.toString(), active.id.toString())
    }
  }

  return (
    <div
      className={`bg-sidebar border-sidebar-border fixed top-0 left-0 z-[45] flex h-[100dvh] w-64 flex-col border-r shadow-sm transition-transform duration-300 dark:shadow-none ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
    >
      {/* Header with Logo */}
      <div className="border-sidebar-border flex items-center border-b p-4">
        <div className="flex h-full items-center gap-1">
          <Zap className="h-5 w-5 text-orange-500" fill="currentColor" />
          <div className="flex flex-col gap-0">
            <div className="flex items-center gap-1">
              <h1 className="text-foreground truncate text-lg leading-5 font-bold">Loadline</h1>
              <span className="mb-3 rounded-md bg-red-900/20 px-[6px] py-[2px] text-[10px] text-red-400">
                Alpha
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Items */}
      <div className="flex-1 overflow-y-auto py-2">
        {/* Main navigation */}
        <div className="mb-4 space-y-1 px-3">
          <UnifiedButton
            variant={location.pathname === '/' ? 'secondary' : 'ghost'}
            className="hover:bg-muted h-11 w-full cursor-pointer justify-start gap-3 px-3"
            onClick={() => handleNavigate('/')}
          >
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1 truncate text-left">Dashboard</span>
            </div>
          </UnifiedButton>

          <UnifiedButton
            variant={location.pathname === '/workouts' ? 'secondary' : 'ghost'}
            className="hover:bg-muted h-11 w-full cursor-pointer justify-start gap-3 px-3"
            onClick={() => handleNavigate('/workouts')}
          >
            <div className="flex items-center gap-2">
              <Dumbbell className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1 truncate text-left">Workouts</span>
            </div>
          </UnifiedButton>

          <UnifiedButton
            variant={location.pathname === '/split' ? 'secondary' : 'ghost'}
            className="hover:bg-muted h-11 w-full cursor-pointer justify-start gap-3 px-3"
            onClick={() => handleNavigate('/split')}
          >
            <div className="flex items-center gap-2">
              <TableOfContents className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1 truncate text-left">Split Builder</span>
            </div>
          </UnifiedButton>
        </div>

        {/* Dashboard sections - only show on dashboard */}
        {showDashboardSections && location.pathname === '/' && (
          <>
            <div className="px-3 pb-2">
              <div className="bg-secondary h-[1px] w-full" />
              <div className="pt-2">
                <span className="text-muted-foreground pl-2 text-xs">Dashboard Sections</span>
              </div>
            </div>

            {sectionsLoading ? (
              <SidebarSectionsSkeleton />
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStartEvent}
                onDragEnd={handleDragEndEvent}
                modifiers={[
                  (args) => {
                    const { transform } = args
                    return {
                      ...transform,
                      x: 0 // Restrict horizontal movement
                    }
                  }
                ]}
              >
                <SortableContext
                  items={orderedSections.map((section) => section.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-1 px-3">
                    {orderedSections.map((section) => {
                      const isExpanded = !collapsedSections[section.id]
                      return (
                        <DraggableSidebarItem
                          key={section.id}
                          section={section}
                          isExpanded={isExpanded}
                          onSectionClick={handleSectionClick}
                          onRemoveSection={
                            onRemoveSection
                              ? (sectionId) => onRemoveSection(sectionId as DashboardSection)
                              : undefined
                          }
                        />
                      )
                    })}

                    {/* Add Sections Button */}
                    <UnifiedButton
                      variant="ghost"
                      onClick={() => handleNavigate('/add-sections')}
                      className="hover:bg-muted border-muted-foreground/30 hover:border-muted-foreground/50 text-muted-foreground hover:text-foreground h-11 w-full cursor-pointer justify-start gap-3 border border-dashed px-3 transition-all duration-200"
                    >
                      <div className="flex items-center gap-2">
                        <Plus className="h-4 w-4 flex-shrink-0" />
                        <span className="flex-1 truncate text-left">Add Sections</span>
                      </div>
                    </UnifiedButton>
                  </div>
                </SortableContext>
              </DndContext>
            )}

            {/* Reset button */}
            <div className="w-full px-3 pt-2">
              <div className="bg-secondary h-[1px] w-full" />
              <div className="flex w-full items-center justify-between pt-1">
                <span className="text-muted-foreground pl-2 text-xs">Drag to reorder</span>
                <Tooltip content="Reset to default" position="right">
                  <UnifiedButton
                    variant="ghost"
                    size="sm"
                    onClick={onResetOrder}
                    className="text-muted-foreground hover:text-foreground h-7 w-full justify-start gap-2 px-2 text-xs"
                  >
                    <RotateCcw className="h-3 w-3" />
                  </UnifiedButton>
                </Tooltip>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Bottom Section */}
      <div className="px-3">
        {bottomSlot ? <div className="mb-3">{bottomSlot}</div> : null}

        {!hideDiscord && (
          <div className="mb-2 flex flex-col gap-1">
            <UnifiedButton
              className="hover:bg-muted h-11 w-full cursor-pointer justify-start gap-3 px-3 text-[#5865F2] hover:text-[#5865F2]"
              variant="ghost"
              onClick={() => window.open(DISCORD_INVITE_URL, '_blank', 'noopener,noreferrer')}
            >
              <div className="flex items-center gap-2">
                <Discord className="h-4 w-4 flex-shrink-0" />
                <span className="flex-1 truncate text-left">Join The Discord</span>
              </div>
            </UnifiedButton>
          </div>
        )}
        <div className="border-sidebar-border flex flex-col gap-2 border-t py-2">
          <div className="space-y-1">
            <UnifiedButton
              variant={location.pathname === '/profile' ? 'secondary' : 'ghost'}
              className="hover:bg-muted h-11 w-full cursor-pointer justify-start gap-3 px-3"
              onClick={() => handleNavigate('/profile')}
            >
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 flex-shrink-0" />
                <span className="flex-1 truncate text-left">Profile</span>
              </div>
            </UnifiedButton>

            <UnifiedButton
              variant={location.pathname === '/settings' ? 'secondary' : 'ghost'}
              className="hover:bg-muted h-11 w-full cursor-pointer justify-start gap-3 px-3"
              onClick={() => handleNavigate('/settings')}
            >
              <div className="flex items-center gap-2">
                <Settings className="text-primary h-4 w-4 flex-shrink-0" />
                <span className="flex-1 truncate text-left">Settings</span>
              </div>
            </UnifiedButton>
          </div>
        </div>
      </div>
    </div>
  )
}
